import type { SuggestionOptions } from "@tiptap/suggestion";
import type { IconType } from "$components/+shared/OVIcon/OVIcon";
import tippy from "tippy.js";
import CommandList from "./CommandList.svelte";
import type { Editor, Range } from "@tiptap/core";
import { startUpload } from "../uploadFiles";
import { get } from "svelte/store";
import { communitySlug } from "app/stores/community";
import { user } from "app/stores/auth";
import {
  AlignLeft,
  At,
  Attachment,
  Bold,
  BulletList,
  Code,
  CodeBlock,
  Doc,
  Exclamation,
  Gif,
  H1,
  H2,
  HashTag,
  Italic,
  NumberedList,
  Quote,
  Rive,
  Table,
  Tabs,
  Toggle,
  Video,
} from "$components/+shared/OVIcon";

export interface Block {
  title: string;
  icon: IconType;
  desc: string;
  command: (props: { editor: Editor; range: Range; props: any }) => void;
}

export interface BlockGroup {
  title: string;
  blocks: Block[];
}

const inline_blocks: Block[] = [
  {
    title: "Mention a channel",
    icon: HashTag,
    desc: "Mention a channel inline",
    command: ({ editor, range }) => {
      editor.chain().focus().deleteRange(range).insertContent("#").run();
    },
  },
  {
    title: "Mention",
    icon: At,
    desc: "Mention a member inline",
    command: ({ editor, range }) => {
      editor.chain().focus().deleteRange(range).insertContent("@").run();
    },
  },
];

const advanced_blocks: Block[] = [
  {
    title: "Docs gallery",
    icon: Doc,
    desc: "Display a gallery of docs or categories",
    command: ({ editor, range }) => {
      editor.chain().focus().deleteRange(range).insertContent({ type: "docsGallery" }).run();
    },
  },
  {
    title: "Doc contents",
    icon: AlignLeft,
    desc: "Show/hide document contents",
    command: ({ editor, range }) => {
      editor
        .chain()
        .focus()
        .deleteRange(range)
        .selectAll()
        .toggleWrap("tableOfContents")
        .setTextSelection(0)
        .run();
    },
  },
  {
    title: "Tabs",
    icon: Tabs,
    desc: "Toggle content using tabs",
    command: ({ editor, range }) => {
      editor.chain().deleteRange(range).addTabBlock().focus().run();
    },
  },
  {
    title: "Table",
    icon: Table,
    desc: "Add a table for structured content",
    command: ({ editor, range }) => {
      editor
        .chain()
        .deleteRange(range)
        .insertTable({ rows: 4, cols: 3, withHeaderRow: true })
        .focus()
        .run();
    },
  },
];

const basic_blocks: Block[] = [
  {
    title: "Heading 1",
    icon: H1,
    desc: "Large section heading",
    command: ({ editor, range }) => {
      editor.chain().focus().deleteRange(range).setNode("heading", { level: 1 }).run();
    },
  },
  {
    title: "Heading 2",
    icon: H2,
    desc: "Medium section heading",
    command: ({ editor, range }) => {
      editor.chain().focus().deleteRange(range).setNode("heading", { level: 2 }).run();
    },
  },
  {
    title: "Heading 3",
    icon: H2,
    desc: "Small section heading",
    command: ({ editor, range }) => {
      editor.chain().focus().deleteRange(range).setNode("heading", { level: 3 }).run();
    },
  },
  {
    title: "Bold",
    icon: Bold,
    desc: "Add bold text",
    command: ({ editor, range }) => {
      editor.chain().focus().deleteRange(range).run();
      editor.chain().focus().toggleBold().run();
    },
  },
  {
    title: "Italic",
    icon: Italic,
    desc: "Add italicised text",
    command: ({ editor, range }) => {
      editor.chain().focus().deleteRange(range).run();
      editor.chain().focus().toggleItalic().run();
    },
  },
  {
    title: "Inline code",
    icon: Code,
    desc: "Add an inline code string",
    command: ({ editor, range }) => {
      editor.chain().focus().deleteRange(range).run();
      editor.chain().focus().toggleCode().run();
    },
  },
  {
    title: "Code block",
    icon: CodeBlock,
    desc: "Capture a code snippet",
    command: ({ editor, range }) => {
      editor.chain().focus().deleteRange(range).toggleCodeBlock().run();
    },
  },
  {
    title: "Quote",
    icon: Quote,
    desc: "Capture a quote",
    command: ({ editor, range }) => {
      editor.chain().focus().deleteRange(range).toggleBlockquote().run();
    },
  },
  {
    title: "Callout",
    icon: Exclamation,
    desc: "Make important info stand out",
    command: ({ editor, range }) => {
      editor.chain().focus().deleteRange(range).toggleCallout().run();
    },
  },
  {
    title: "Bulleted list",
    icon: BulletList,
    desc: "Add a bulleted list",
    command: ({ editor, range }) => {
      editor.chain().focus().deleteRange(range).toggleBulletList().run();
    },
  },
  {
    title: "Numbered list",
    icon: NumberedList,
    desc: "Add a numbered list",
    command: ({ editor, range }) => {
      editor.chain().focus().deleteRange(range).toggleOrderedList().run();
    },
  },
  {
    title: "Accordion",
    icon: Toggle,
    desc: "Hide & show content inside",
    command: ({ editor, range }) => {
      editor.chain().focus().deleteRange(range).run();
      editor.chain().focus().setDetails().run();
    },
  },
];

const embed_blocks: Block[] = [
  {
    title: "Embed file / image / video",
    icon: Attachment,
    desc: "Embed an image, video or any other file type",
    command: ({ editor, range }) => {
      editor.chain().focus().deleteRange(range).run();

      // programatically create a file input element + click it
      const input = document.createElement("input");
      input.type = "file";
      input.multiple = true; // allow selection of multiple files
      input.click();

      input.onchange = (e) => {
        const selectedFiles = (e.target as HTMLInputElement).files;
        if (selectedFiles) {
          const metadata = {
            user: get(user)!.uuid!,
            community: get(communitySlug),
          };
          const pos = editor.state.selection.anchor;

          Array.from(selectedFiles).forEach((file: File) =>
            startUpload(file, editor.view, pos, metadata)
          );
        }
      };
    },
  },
  {
    title: "GIF",
    icon: Gif,
    desc: "Add a GIF from giphy",
    command: ({ editor, range }) => {
      editor.chain().focus().deleteRange(range).insertContent("/giphy ").run();
    },
  },
  {
    title: "YouTube",
    icon: Video,
    desc: "Embed a YouTube video",
    command: ({ editor, range }) => {
      const url = window.prompt("URL");
      if (!url) return;
      editor.chain().focus().deleteRange(range).setYoutubeVideo({ src: url }).run();
    },
  },
  {
    title: "CodeSandbox",
    icon: CodeBlock,
    desc: "Embed a codesandbox.io sandbox",
    command: ({ editor, range }) => {
      const url = window.prompt("URL");
      if (!url) return;
      editor.chain().focus().deleteRange(range).setCodeSandboxBlock({ src: url }).run();
    },
  },
  {
    title: "Rive",
    icon: Rive,
    desc: "Embed a Rive",
    command: ({ editor, range }) => {
      const url = window.prompt("URL");
      if (!url) return;
      editor.chain().focus().deleteRange(range).setRiveBlock({ src: url }).run();
    },
  },
  // Graphy option temporarily hidden until more stable on their side (see ENG-1116)
  // {
  //   title: "Graphy",
  //   icon: Graphy,
  //   desc: "Embed a Graphy graph",
  //   command: ({ editor, range }) => {
  //     const url = window.prompt("URL");
  //     if (!url) return;
  //     editor.chain().focus().deleteRange(range).setGraphyBlock({ src: url }).run();
  //   },
  // },
  // {
  //   title: "Figma",
  //   icon: faPencilPaintbrush,
  //   desc: "Embed Figma Canvas",
  //   command: ({ editor, range }) => {
  //     const url = window.prompt('URL')
  //     if (!url) return;
  //     editor.chain().focus().deleteRange(range).setFigmaBlock({src: url}).run();
  //   },
  // },
  // {
  //   title: "Loom",
  //   icon: faCircleVideo,
  //   desc: "Embed a Loom video",
  //   command: ({ editor, range }) => {
  //     const url = window.prompt('URL')
  //     if (!url) return;
  //     editor.chain().focus().deleteRange(range).setLoomVideo({src: url}).run();
  //   },
  // },
];

const suggestion = ({
  basic = true,
  advanced = true,
  inline = true,
  embeds = true,
}: {
  basic?: boolean;
  advanced?: boolean;
  inline?: boolean;
  embeds?: boolean;
}): Partial<SuggestionOptions> => {
  const block_group: { [name: string]: BlockGroup } = {};

  if (basic) {
    block_group.basic = {
      title: "Formatting",
      blocks: [...basic_blocks],
    };
  }

  if (inline) {
    block_group.inline = {
      title: "Inline mentions",
      blocks: [...inline_blocks],
    };
  }

  if (advanced) {
    block_group.advanced = {
      title: "Organization blocks",
      blocks: [...advanced_blocks],
    };
  }

  if (embeds) {
    block_group.embeds = {
      title: "Embeds",
      blocks: [...embed_blocks],
    };
  }

  return {
    items: ({ query }: { query: string }) => {
      return Object.values(block_group)
        .map(({ title, blocks }) => ({
          title,
          blocks: blocks.filter((item) => item.title.toLowerCase().includes(query.toLowerCase())),
        }))
        .filter(({ blocks }) => blocks.length > 0);
    },

    render: () => {
      let component: CommandList | null;
      let popup: any[];

      return {
        onStart: (props) => {
          const el = document.createElement("div");
          component = new CommandList({
            target: el,
            props,
          });

          if (!props.clientRect) {
            return;
          }

          // @ts-ignore - incorrect typing https://github.com/ueberdosis/tiptap/issues/2795
          popup = tippy("body", {
            getReferenceClientRect: props.clientRect,
            appendTo: () => document.body,
            content: el,
            showOnCreate: true,
            interactive: true,
            trigger: "manual",
            placement: "bottom-start",
          });
        },

        onUpdate(props) {
          component?.$set(props);

          if (!props.clientRect) {
            return;
          }

          popup[0].setProps({
            getReferenceClientRect: props.clientRect,
          });
        },

        onKeyDown(props) {
          if (props.event.key === "Escape") {
            popup[0].hide();

            return true;
          }
          return !!component?.onKeyDown(props);
        },

        onExit() {
          popup?.[0].destroy();
          component?.$destroy();
        },
      };
    },
  };
};

export default suggestion;
